import React, { useMemo, useState, useEffect } from "react";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

import { saveProfile } from "../../services/mosqueContentRepository";
import useMosqueProfail from "./hooks/useMosqueProfail";
import { buildHirarkiByRole, cleanPhone, normalizeCategoryKey, slugify } from "./utils/profailUtils";

import ProfailImages from "./components/ProfailImages";
import ProfailFields from "./components/ProfailFields";
import PegawaiSection from "./components/PegawaiSection";
import BankAccountsSection from "./components/BankAccountsSection";
import SectionCard from "./components/SectionCard";
import WebsiteCard from "./components/WebsiteCard";


import "../../styles/Profail.css";

export default function ProfailPage() {
    const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const mosqueId = userProfile.mosqueId || "";
    const namaMasjid = userProfile.namaMasjid || "";
    const emailMasjid = userProfile.emailMasjid || "";

    const [editMode, setEditMode] = useState(false);

    const { loading, original, setOriginal, draft, setDraft, cats } = useMosqueProfail({
        mosqueId,
        fallbackNama: namaMasjid,
        fallbackEmel: emailMasjid,
    });

    // manage cats bila add custom
    const [catsState, setCatsState] = useState(null);
    const catsMerged = useMemo(() => catsState || cats, [catsState, cats]);

    useEffect(() => {
        if (!catsState && cats?.length) setCatsState(cats);
    }, [cats, catsState]);

    const handleEdit = () => setEditMode(true);

    const handleCancel = () => {
        setDraft(original);
        setEditMode(false);
    };

    const handleSave = async () => {
        if (!mosqueId || !draft) return;

        try {
            const payload = {
                ...draft,
                slug: slugify(draft.slug || ""),
                no_whatsapp: cleanPhone(draft.no_whatsapp || ""),
                pegawai: (draft.pegawai || []).map((p) => {
                    const category = normalizeCategoryKey(p.category || "pengurusan");
                    const roles = Array.isArray(p.roles) && p.roles.length ? p.roles : ["pengurusan"];
                    const baseHirarki = Number(p.hirarki ?? 99);

                    const hirarkiByRole = buildHirarkiByRole({
                        category,
                        roles,
                        baseHirarki,
                    });

                    return {
                        id: p.id || undefined,
                        category,
                        label: p.label || category,
                        nama: p.nama || "",
                        jawatan: p.jawatan || "",
                        gambar: p.gambar || "",
                        hirarki: baseHirarki,
                        roles,
                        hirarkiByRole,
                    };
                }),
                bankAccounts: (draft.bankAccounts || []).filter((b) => b.bank || b.account || b.holder),
            };

            await saveProfile(mosqueId, payload);

            // optional local storage
            localStorage.setItem("userProfile", JSON.stringify({ ...userProfile, profail: payload }));

            setOriginal(payload);
            setDraft(payload);
            setEditMode(false);
            toast.success("Data berjaya dikemaskini");
        } catch (err) {
            console.error(err);
            toast.error("Gagal kemaskini data");
        }
    };

    if (!mosqueId) return <div className="pv2-container">Tiada mosqueId dalam userProfile.</div>;
    if (loading || !draft) return <div className="pv2-container">Loading...</div>;

    return (
        <div className="pv2-container">
            {/* ===== Topbar CMS ===== */}
            <div className="pv2-topbar">
                <div>
                    <div className="pv2-page-title">Profail Masjid</div>
                    <div className="pv2-page-sub">
                        Kemas kini maklumat untuk paparan web.
                        {editMode ? <span className="pv2-badge">MODE EDIT</span> : null}
                    </div>
                </div>

                <div className="pv2-actions">
                    {!editMode ? (
                        <button className="button" onClick={handleEdit}>Edit</button>
                    ) : (
                        <>
                            <button className="button" onClick={handleSave}>Save</button>
                            <button className="button" onClick={handleCancel}>Cancel</button>
                        </>
                    )}
                </div>
            </div>

            {/* ===== 2 Column Layout ===== */}
            <div className="pv2-grid">
                <div className="pv2-main">
                    <SectionCard title="Maklumat Asas" desc="Nama, poskod, alamat, bandar, negeri, telefon, emel.">
                        <ProfailFields editMode={editMode} draft={draft} setDraft={setDraft} />
                    </SectionCard>



                    <SectionCard title="Pegawai Masjid" desc="Senarai pegawai ikut kategori + paparan Imam/Bilal jika ditanda.">
                        <PegawaiSection
                            mosqueId={mosqueId}
                            editMode={editMode}
                            draft={draft}
                            setDraft={setDraft}
                            cats={catsMerged}
                            setCats={setCatsState}
                        />
                    </SectionCard>


                </div>

                <div className="pv2-side">
                    <WebsiteCard editMode={editMode} draft={draft} setDraft={setDraft} />

                    <SectionCard title="Media" desc="Logo, gambar masjid & DuitNow QR.">
                        <ProfailImages mosqueId={mosqueId} mosqueData={draft} />
                    </SectionCard>

                    <SectionCard title="Maklumat Bank" desc="Akaun bank untuk paparan umum.">
                        <BankAccountsSection editMode={editMode} draft={draft} setDraft={setDraft} />
                    </SectionCard>
                </div>


            </div>

        </div>
    );
}
